import { useTranslation } from 'react-i18next';
import { Disclosure, DisclosureButton, DisclosurePanel, Transition } from '@headlessui/react';
import { ChevronDown } from 'lucide-react';
import { PageTransition, StaggerItem } from '../app/page-transition.js';

type FaqEntry = { id: string; questionKey: string; answerKey: string };

const FAQ_ITEMS: ReadonlyArray<FaqEntry> = [
  {
    id: 'what-is-agentbrush',
    questionKey: 'faq.item.what-is-agentbrush.question',
    answerKey: 'faq.item.what-is-agentbrush.answer',
  },
  {
    id: 'which-clis',
    questionKey: 'faq.item.which-clis.question',
    answerKey: 'faq.item.which-clis.answer',
  },
  {
    id: 'how-much-does-it-cost',
    questionKey: 'faq.item.how-much-does-it-cost.question',
    answerKey: 'faq.item.how-much-does-it-cost.answer',
  },
  {
    id: 'what-is-a-token',
    questionKey: 'faq.item.what-is-a-token.question',
    answerKey: 'faq.item.what-is-a-token.answer',
  },
  {
    id: 'do-you-store-images',
    questionKey: 'faq.item.do-you-store-images.question',
    answerKey: 'faq.item.do-you-store-images.answer',
  },
  {
    id: 'is-there-a-free-trial',
    questionKey: 'faq.item.is-there-a-free-trial.question',
    answerKey: 'faq.item.is-there-a-free-trial.answer',
  },
  {
    id: 'how-do-i-cancel',
    questionKey: 'faq.item.how-do-i-cancel.question',
    answerKey: 'faq.item.how-do-i-cancel.answer',
  },
  {
    id: 'where-can-i-get-help',
    questionKey: 'faq.item.where-can-i-get-help.question',
    answerKey: 'faq.item.where-can-i-get-help.answer',
  },
];

export function FaqPage() {
  const { t } = useTranslation('content');

  return (
    <PageTransition>
      <StaggerItem>
        <h1 className="text-h1 font-semibold text-primary mb-3">{t('faq.heading')}</h1>
      </StaggerItem>
      <StaggerItem>
        <p className="text-body text-secondary mb-6 max-w-[640px]">{t('faq.intro')}</p>
      </StaggerItem>
      <StaggerItem>
        <ul className="flex flex-col divide-y divide-white/[.04] max-w-[800px] mx-auto">
          {FAQ_ITEMS.map((item) => (
            <li key={item.id}>
              <Disclosure
                as="div"
                data-testid="faq-item"
                data-faq-id={item.id}
                className="bg-white/[.03] backdrop-blur-sm border border-white/[.04] rounded-lg my-1"
              >
                <DisclosureButton className="group w-full text-left px-6 py-4 flex items-center justify-between text-primary text-body font-medium">
                  <span>{t(item.questionKey)}</span>
                  <ChevronDown
                    aria-hidden="true"
                    className="size-5 text-secondary motion-safe:transition-transform motion-safe:duration-150 group-data-[open]:rotate-180"
                  />
                </DisclosureButton>
                <Transition
                  enter="motion-safe:transition-opacity motion-safe:duration-150 ease-out"
                  enterFrom="opacity-0"
                  enterTo="opacity-100"
                  leave="motion-safe:transition-opacity motion-safe:duration-100 ease-in"
                  leaveFrom="opacity-100"
                  leaveTo="opacity-0"
                >
                  <DisclosurePanel className="px-6 pb-4 text-body text-secondary">
                    {t(item.answerKey)}
                  </DisclosurePanel>
                </Transition>
              </Disclosure>
            </li>
          ))}
        </ul>
      </StaggerItem>
    </PageTransition>
  );
}
